import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { chartTheme } from '../widgets/chart-theme'
import { formatInteger } from 'utils/formatting'
import { seriesColor } from '../chart-palette'
import { QueryResult, ViewConfig } from '../view-config'

/**
 * recharts renderer for a dashboard *tile's* chart body, fed by the engine's
 * aggregate QueryResult. This is the new (Figma) chart treatment applied to the
 * Sales Overview tiles — the report detail/builder pages still use the Chart.js
 * `ReportChart`. `variant` picks the visualization (the tile declares it):
 *   - 'bar'     single-series bar over groups
 *   - 'stacked' stacked bar over groups × series
 */

export type TileChartVariant = 'bar' | 'stacked'

const TICK = { fill: chartTheme.axis, fontSize: 12 } as const

/** Tooltip card matching the standalone widgets' style. */
function TileTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: {
    name?: string
    value: number
    color?: string
    payload?: { fill?: string }
  }[]
  label?: string
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-line bg-surface px-3 py-2 shadow-sm">
      {label != null && (
        <p className="mb-1 text-xs font-semibold text-fg-secondary">{label}</p>
      )}
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 py-0.5">
          <span
            className="inline-block size-2 shrink-0 rounded-full"
            style={{
              backgroundColor:
                entry.color ?? entry.payload?.fill ?? chartTheme.primary,
            }}
          />
          <span className="text-sm font-medium text-fg">
            {entry.name ? `${entry.name}: ` : ''}
            {formatInteger(entry.value)}
          </span>
        </div>
      ))}
    </div>
  )
}

export function DashboardTileChart({
  variant,
  config,
  result,
}: {
  variant: TileChartVariant
  config: ViewConfig
  result: QueryResult
}) {
  if (result.kind !== 'aggregate') return null
  const metricLabel = config.chartMetric ?? config.metrics?.[0]?.label ?? ''

  if (variant === 'stacked' && result.seriesKeys) {
    const { seriesKeys } = result
    const data = result.groups.map((g) => {
      const row: Record<string, string | number> = { name: g.key }
      seriesKeys.forEach((sk) => {
        row[sk] = g.series?.[sk]?.[metricLabel] ?? 0
      })
      return row
    })
    return (
      <div className="h-full min-h-0 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 4, right: 4, left: 0, bottom: 4 }}
          >
            <XAxis
              dataKey="name"
              axisLine={{ stroke: chartTheme.grid }}
              tickLine={false}
              tick={TICK}
              dy={8}
              height={24}
            />
            <YAxis
              axisLine={{ stroke: chartTheme.grid }}
              tickLine={false}
              tick={TICK}
              width={44}
            />
            <Tooltip
              cursor={{ fill: 'transparent' }}
              content={<TileTooltip />}
            />
            {/* Flat segments: with sparse data the topmost non-zero series
                varies per column, so a fixed radius would round some bars and
                not others. */}
            {seriesKeys.map((sk, i) => (
              <Bar
                key={sk}
                dataKey={sk}
                stackId="a"
                fill={seriesColor(i)}
                maxBarSize={28}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    )
  }

  // Default: single-series bar.
  const data = result.groups.map((g) => ({
    name: g.key,
    value: g.metrics[metricLabel] ?? 0,
  }))
  return (
    <div className="h-full min-h-0 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <XAxis
            dataKey="name"
            axisLine={{ stroke: chartTheme.grid }}
            tickLine={false}
            tick={TICK}
            dy={6}
            height={22}
          />
          <YAxis
            axisLine={{ stroke: chartTheme.grid }}
            tickLine={false}
            tick={TICK}
            width={32}
          />
          <Tooltip cursor={{ fill: 'transparent' }} content={<TileTooltip />} />
          <Bar
            dataKey="value"
            fill={chartTheme.primary}
            radius={[6, 6, 0, 0]}
            maxBarSize={28}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
